import { el, btn, backButton } from '@/ui/dom';
import { sfxCollect, sfxUiTick } from '@/audio/sfx';
import { hapticImpact, hapticTick } from '@/ui/haptics';
import { confirmAction } from '@/ui/confirm';
import { lockCta, type LockCtaOptions } from '@/ui/lockCta';
import { skillTier } from '@/core/skillPoints';
import { USERNAME_MAX } from '@/account/racerNames';
import type { SocialEvent, SocialService } from '@/social/SocialService';
import {
  CREW_MAX,
  CREW_NAME_MAX,
  CREW_SORTS,
  DEFAULT_CREST,
  createCrewMessage,
  crewRaceWhat,
  crewTotals,
  defaultHelmet,
  friendRequestMessage,
  inviteMessage,
  isCrewRaceFresh,
  isValidCrewName,
  joinCrewMessage,
  rankMembers,
  rankValue,
  sanitizeCrewCode,
  suggestCrewName,
  tidyCrewName,
  type Crest,
  type CrewMember,
  type CrewOverview,
  type CrewRaceChoice,
  type CrewRaceInvite,
  type CrewSort,
  type FriendRow,
  type FriendsOverview,
  type Helmet,
} from '@/social/crewLogic';
import { captainBadgeSvg, helmetEl, helmetLabel } from '@/ui/social/helmetArt';
import { crestEl, crestLabel } from '@/ui/social/crestArt';
import {
  openCrestPicker,
  openCrewSettings,
  openHelmetPicker,
  openInviteSheet,
  openMemberSheet,
  moderateLink,
} from '@/ui/social/crewSheets';
import { openPlayerSheet, type ModerationDeps } from '@/ui/social/playerSheet';
import { openClubRaceSheet } from '@/ui/social/clubRaceSheet';
import { closeSheet, openSheet, sheetInput } from '@/ui/social/sheet';
import {
  CHEVRON_SVG,
  circleButton,
  footLine,
  panelHead,
  ridButton,
  type RidButton,
} from '@/ui/social/clubUi';

/**
 * RaceCrewScreen.ts — the RACER CLUB screen (M140 as "Race Crew"; M141
 * renamed it and rebuilt it in the Racer ID dashboard's language, owner:
 * "match the screen and modernized button design aesthetic of the Racer
 * Dashboard"). Internal names keep M140's crew* (contracts players never see).
 *
 * ONE gold-framed card on the Dribble Racer HQ photo, like the dashboard:
 *
 *   HERO   — the club art (public/club/hero.webp, a swappable drop-in: the
 *            owner's art replaces the stand-in with one file) under the
 *            dashboard scrim, carrying RACER CLUB, the club CREST (M142: the
 *            captain's icon in the club's two colours) + name, then two round
 *            buttons that read as a pair: the blue INVITE circle (every
 *            member may invite) and the captain's gear (the dashboard's
 *            rename-pencil spot); the captain pill and the members line. No
 *            club yet: START YOUR RACER CLUB, with a crest that previews the
 *            one being designed in the create panel.
 *   TABS   — MY CLUB · FRIENDS (gold underline on the live tab, badges).
 *   BODY   — MY CLUB in a club (M142 layout): START / JOIN CLUB RACE heads a
 *            left column over the four colour-coded club stats and MY
 *            HELMET, and the CLUB RANKINGS (this week / all time / wins,
 *            every racer's helmet next to their name) start level with the
 *            race button on the right; then any invites from other clubs,
 *            the foot line and LEAVE CLUB. START CLUB RACE opens the club
 *            race sheet (quick race or tour, the city or the stops, club
 *            only or open). No club: CREATE (a name already checked free +
 *            the club crest) beside JOIN (invites + a club code). FRIENDS:
 *            add by Racer ID or share your friend link, requests, your
 *            friends (one-tap INVITE to your club) and the requests you sent.
 *
 * Layouts: desktop / tablet = one centred card, the two-column club body;
 * landscape phones = the dashboard's split (hero left, tabs + everything else
 * right, scrolling) with the club body in one column; portrait phones = one
 * column with the rankings straight after the race button.
 *
 * Reached from PLAY ONLINE and from the Racer ID dashboard (owner rule). On
 * the web it is for game OWNERS: a non-owner sees the page under the lock
 * banner, every action routed to the purchase screen (Flow decides).
 * Every dynamic string renders via textContent.
 */

export interface RaceCrewCallbacks {
  social: SocialService;
  onBack: () => void;
  /** Open a club race (Flow makes the room + sends the club the invite) —
   *  M142: a single race or a tour, club only or open. Resolves an error
   *  line, or null once the lobby is opening. */
  onStartRace: (choice: CrewRaceChoice) => Promise<string | null>;
  /** Walk into the club's open race. */
  onJoinRace: (race: CrewRaceInvite) => void;
  shareCrewLink: (crewName: string, code: string) => Promise<'shared' | 'copied' | 'dismissed'>;
  shareFriendLink: () => Promise<'shared' | 'copied' | 'dismissed'>;
  copyText: (text: string) => Promise<boolean>;
  /** After creating / joining a club (Flow may soft-ask for iOS pushes). */
  onJoinedCrew?: (crewName: string) => void;
  reducedMotion: () => boolean;
  /** M156: REPORT + BLOCK (App Store 1.2) from member / friend / request /
   *  invite cards. */
  moderation: ModerationDeps;
}

export type CrewTab = 'crew' | 'friends';

const FLAG_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M5 2.8h1.6V22H5zM7.4 3.4h12.4l-2.6 4.4 2.6 4.4H7.4z" opacity=".95"/>' +
  '<path d="M9 4.6h2.4V7H9zm4.8 0h2.4V7h-2.4zM11.4 7h2.4v2.4h-2.4zM9 9.4h2.4v2.4H9zm4.8 0h2.4v2.4h-2.4z" fill="#000" opacity=".45"/></svg>';
const ADD_PERSON_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="9" cy="8.2" r="3.6"/><path d="M2.4 19.6c0-3.5 3-5.9 6.6-5.9s6.6 2.4 6.6 5.9v.8H2.4z"/>' +
  '<path d="M18.2 7.4h1.8v2.8h2.8V12H20v2.8h-1.8V12h-2.8v-1.8h2.8z"/></svg>';
const SHARE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="6" cy="12" r="2.6"/><circle cx="17.5" cy="5.6" r="2.6"/><circle cx="17.5" cy="18.4" r="2.6"/>' +
  '<path d="m8.3 10.9 6.9-3.9 1 1.7-6.9 3.9zM8.3 13.1l6.9 3.9 1-1.7-6.9-3.9z"/></svg>';
const DICE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2.5 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM7.5 15a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg>';
const GEAR_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8Zm8.9 5.1-1.9 3.3-2.3-.5a7 7 0 0 1-1.6.9l-.7 2.3h-3.8l-.7-2.3a7 7 0 0 1-1.6-.9l-2.3.5-1.9-3.3 1.6-1.7a7.3 7.3 0 0 1 0-1.9L4.1 8.3 6 5l2.3.5a7 7 0 0 1 1.6-.9l.7-2.3h3.8l.7 2.3c.6.2 1.1.5 1.6.9L19 5l1.9 3.3-1.6 1.7c.1.6.1 1.3 0 1.9l1.6 1.8Z"/></svg>';
/** The MY CLUB tab: the club shield (the hub card's glyph language). */
const SHIELD_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 1.8 21 4.6v6.1c0 5.5-3.8 9.9-9 11.5-5.2-1.6-9-6-9-11.5V4.6z" opacity=".35"/>' +
  '<path d="M12 4.6 18.6 6.7v4.2c0 4.1-2.8 7.4-6.6 8.7-3.8-1.3-6.6-4.6-6.6-8.7V6.7z"/></svg>';
const PEOPLE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="8.5" cy="8" r="3.4"/><path d="M2.2 19.4c0-3.4 2.8-5.8 6.3-5.8s6.3 2.4 6.3 5.8v.6H2.2z"/>' +
  '<circle cx="16.6" cy="8.8" r="2.7" opacity=".7"/><path d="M16 13.7c3.2 0 5.8 2.1 5.8 5.2v.6h-5.2c0-2.3-.9-4.3-2.5-5.6.6-.1 1.2-.2 1.9-.2Z" opacity=".7"/></svg>';

const fmt = (n: number): string => n.toLocaleString('en-US');

/** The hero name's size step (club names run 3-18 characters; long ones may
 *  wrap onto two lines rather than shrink to nothing on a phone). One very
 *  long WORD steps down further, so it wraps between words, not inside one. */
function nameSizeClass(name: string): string {
  const longestWord = Math.max(0, ...name.split(/\s+/).map((w) => w.length));
  if (longestWord > 12) return 'club-name-l club-name-xl';
  if (name.length > 12) return 'club-name-l';
  if (name.length > 8) return 'club-name-m';
  return '';
}

export class RaceCrewScreen {
  private readonly root: HTMLDivElement;
  private readonly shell: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private readonly heroCopy: HTMLDivElement;
  private readonly crewTabBtn: HTMLButtonElement;
  private readonly friendsTabBtn: HTMLButtonElement;
  private readonly crewBadge: HTMLSpanElement;
  private readonly friendsBadge: HTMLSpanElement;
  private readonly body: HTMLDivElement;
  private lockEl: HTMLDivElement | null = null;
  private locked = false;
  /** The no-club hero's crest — it previews the crest being designed. */
  private startCrest: HTMLDivElement | null = null;

  private tab: CrewTab = 'crew';
  private sort: CrewSort = 'week';
  private crew: CrewOverview | null = null;
  private friends: FriendsOverview | null = null;
  private crewSeq = 0;
  private friendsSeq = 0;
  /** The first load failed (offline) — show TRY AGAIN, not a spinner forever. */
  private crewFailed = false;
  private friendsFailed = false;
  /** A name the player is typing in the create field — survives repaints. */
  private draftName: string | null = null;
  /** The crest picked in the create panel (null = the soccer-ball default) —
   *  survives repaints like the name does. */
  private draftCrest: Crest | null = null;
  /** The LIVE create panel's crest repaint (reassigned every render), so a
   *  crest picked while the screen re-rendered underneath the sheet (a live
   *  invite reloads the club) lands on the panel the player can see. */
  private paintCreateCrest: (() => void) | null = null;
  /** A CREATE CLUB is in flight. It outlives a re-render (a tab switch or a
   *  live reload rebuilds the panel), so the new panel comes up locked too and
   *  the answer lands on the live controls below, never on detached ones. */
  private creating = false;
  private createCtl: {
    create: RidButton;
    crestBtn: RidButton;
    status: HTMLDivElement;
  } | null = null;
  private suggestion = '';
  /** A create / join / helmet / invite step is running (no live repaint). */
  private flowBusy = false;
  private inboxCrewInvites = 0;
  private inboxFriendRequests = 0;
  /** Whose data the caches hold (M140b: a sign-out or a different Racer ID
   *  must never see the previous account's club, friends or invites). */
  private accountId: string | null = null;
  /** Debounced reloads for live events (a burst of changes = one reload). */
  private crewReload: number | null = null;
  private friendsReload: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly cb: RaceCrewCallbacks,
  ) {
    this.root = el('div', 'screen screen-crew', parent);
    this.root.style.display = 'none';
    this.shell = el('div', 'crew-shell', this.root);
    this.card = el('div', 'club-card', this.shell);

    // ---- hero: the club art under the dashboard scrim ----
    const hero = el('div', 'rid-hero club-hero', this.card);
    const img = el('img', 'rid-hero-img club-hero-img', hero);
    img.alt = '';
    img.decoding = 'async';
    img.src = `${import.meta.env.BASE_URL}club/hero.webp`;
    img.onerror = () => img.remove(); // the hero's own gold-glow base stands in
    el('div', 'rid-hero-scrim', hero);
    this.heroCopy = el('div', 'rid-hero-copy club-hero-copy', hero);

    // ---- tabs ----
    const tabs = el('div', 'club-tabs', this.card);
    tabs.setAttribute('role', 'tablist');
    const tabBtn = (
      id: CrewTab,
      icon: string,
      label: string,
    ): [HTMLButtonElement, HTMLSpanElement] => {
      const b = el('button', 'club-tab', tabs);
      b.type = 'button';
      b.setAttribute('role', 'tab');
      el('span', 'club-tab-ic', b).innerHTML = icon;
      el('span', 'club-tab-label', b).textContent = label;
      const badge = el('span', 'crew-tab-badge', b);
      badge.style.display = 'none';
      b.addEventListener('click', () => {
        if (this.tab === id) return;
        this.tab = id;
        this.paintTabs();
        this.render();
        this.scrollTop();
        this.load();
      });
      return [b, badge];
    };
    [this.crewTabBtn, this.crewBadge] = tabBtn('crew', SHIELD_SVG, 'MY CLUB');
    [this.friendsTabBtn, this.friendsBadge] = tabBtn('friends', PEOPLE_SVG, 'FRIENDS');

    this.body = el('div', 'club-body', this.card);
    backButton(this.root, () => this.cb.onBack());
  }

  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  show(opts: { tab?: CrewTab; lock?: LockCtaOptions } = {}): void {
    this.tab = opts.tab ?? this.tab;
    this.lockEl?.remove();
    this.lockEl = null;
    this.locked = !!opts.lock;
    if (opts.lock) {
      this.lockEl = lockCta(this.shell, { ...opts.lock, className: 'crew-lockcta' });
      this.shell.insertBefore(this.lockEl, this.card);
    }
    this.root.classList.toggle('crew-locked', this.locked);
    this.root.classList.toggle('reduced', this.cb.reducedMotion());
    this.root.style.display = '';
    this.paintTabs();
    this.render();
    this.scrollTop();
    this.load();
  }

  /** A fresh visit or a tab switch starts at the top (the landscape layout
   *  scrolls the body; the other layouts scroll the screen). */
  private scrollTop(): void {
    this.body.scrollTop = 0;
    this.root.scrollTop = 0;
  }

  hide(): void {
    this.root.style.display = 'none';
    closeSheet();
  }

  /** A club was joined from OUTSIDE this screen (a club link) — run the same
   *  welcome a join from here gets (helmet pick, then the club page). */
  welcomeToCrew(crewName: string): void {
    this.tab = 'crew';
    this.paintTabs();
    this.afterJoin(crewName, 'joined');
  }

  /** The signed-in Racer ID changed (sign-in, sign-out, another player):
   *  drop everything cached and drop any answer still in flight. */
  setAccount(userId: string | null): void {
    if (userId === this.accountId) return;
    this.accountId = userId;
    this.crew = null;
    this.friends = null;
    this.crewFailed = false;
    this.friendsFailed = false;
    this.draftName = null;
    this.draftCrest = null;
    this.suggestion = '';
    this.inboxCrewInvites = 0;
    this.inboxFriendRequests = 0;
    this.flowBusy = false;
    this.crewSeq++;
    this.friendsSeq++;
    if (this.isVisible) {
      closeSheet();
      this.paintTabs();
      this.render();
      if (!this.locked) this.load();
    }
  }

  /** The inbox counts (badges on the tabs). */
  setInbox(crewInvites: number, friendRequests: number): void {
    this.inboxCrewInvites = crewInvites;
    this.inboxFriendRequests = friendRequests;
    this.paintTabs();
  }

  /** Something changed server-side — refresh what's on screen. */
  onSocial(e: SocialEvent): void {
    if (!this.isVisible || this.locked) return;
    switch (e.type) {
      case 'crew_changed':
      case 'crew_removed':
      case 'crew_invite':
      case 'crew_race':
      case 'crew_race_closed':
        if (this.crewReload === null) {
          this.crewReload = window.setTimeout(() => {
            this.crewReload = null;
            if (this.isVisible && !this.locked) this.loadCrew();
          }, 800);
        }
        break;
      case 'friend_request':
      case 'friend_added':
        if (this.friendsReload === null) {
          this.friendsReload = window.setTimeout(() => {
            this.friendsReload = null;
            if (this.isVisible && !this.locked) this.loadFriends();
          }, 800);
        }
        break;
      default:
        break;
    }
  }

  // ---- data -------------------------------------------------------------------------------

  private load(): void {
    if (this.locked) return;
    this.loadCrew();
    // The friends list feeds the club INVITE sheet too — always keep it warm.
    this.loadFriends();
  }

  private loadCrew(): void {
    const seq = ++this.crewSeq;
    void this.cb.social.crewOverview().then((o) => {
      if (seq !== this.crewSeq) return;
      if (o) {
        this.crew = o;
        this.crewFailed = false;
      } else if (!this.crew) {
        this.crewFailed = true;
      }
      this.paintTabs();
      if (!this.isVisible || this.flowBusy) return;
      // The hero shows the club on BOTH tabs; the body only on MY CLUB.
      if (this.tab === 'crew') this.render();
      else this.renderHero();
    });
  }

  private loadFriends(): void {
    const seq = ++this.friendsSeq;
    void this.cb.social.friendsOverview().then((f) => {
      if (seq !== this.friendsSeq) return;
      if (f) {
        this.friends = f;
        this.friendsFailed = false;
      } else if (!this.friends) {
        this.friendsFailed = true;
      }
      this.paintTabs();
      if (this.isVisible && this.tab === 'friends' && !this.flowBusy) this.render();
    });
  }

  private paintTabs(): void {
    this.crewTabBtn.classList.toggle('on', this.tab === 'crew');
    this.friendsTabBtn.classList.toggle('on', this.tab === 'friends');
    this.crewTabBtn.setAttribute('aria-selected', String(this.tab === 'crew'));
    this.friendsTabBtn.setAttribute('aria-selected', String(this.tab === 'friends'));
    const invites = this.crew
      ? this.crew.crew
        ? 0
        : this.crew.invites.length
      : this.inboxCrewInvites;
    const requests = this.friends ? this.friends.incoming.length : this.inboxFriendRequests;
    const race = this.crew?.race && isCrewRaceFresh(this.crew.race) ? 1 : 0;
    const crewN = invites + race;
    this.crewBadge.textContent = race ? 'LIVE' : String(crewN);
    this.crewBadge.classList.toggle('live', race > 0);
    this.crewBadge.style.display = crewN > 0 ? '' : 'none';
    this.friendsBadge.textContent = String(requests);
    this.friendsBadge.style.display = requests > 0 ? '' : 'none';
  }

  // ---- render -------------------------------------------------------------------------------

  private render(): void {
    this.renderHero();
    this.body.replaceChildren();
    this.body.className = `club-body club-body-${this.tab}`;
    if (this.tab === 'friends') {
      this.renderFriends();
      return;
    }
    if (this.locked) {
      this.renderNoCrew(); // the preview a non-owner browses under the banner
      return;
    }
    if (this.crew === null) {
      if (this.crewFailed) this.renderRetry(this.body);
      else this.renderLoading();
      return;
    }
    if (this.crew.crew) this.renderCrew(this.crew);
    else this.renderNoCrew();
  }

  /** The hero copy: the club identity (both tabs), or the START YOUR RACER
   *  CLUB pitch, in the dashboard's kicker / gold line / big name voice. */
  private renderHero(): void {
    const copy = this.heroCopy;
    copy.replaceChildren();
    this.startCrest = null;
    el('div', 'rid-kicker', copy).textContent = 'RACER CLUB';
    const c = this.locked ? null : (this.crew?.crew ?? null);

    if (c && this.crew) {
      const o = this.crew;
      const lockup = el('div', 'club-lockup', copy);
      lockup.appendChild(crestEl(c.crest, 'crew-crest club-crest', crestLabel(c.crest)));
      const text = el('div', 'club-lockup-text', lockup);
      el('div', 'rid-welcome', text).textContent = 'YOUR CLUB';
      const row = el('div', 'club-name-row', text);
      const name = el('div', `club-name ${nameSizeClass(c.name)}`, row);
      name.textContent = c.name.toUpperCase();
      // M142: INVITE (any member) + the captain's gear, a pair of circles that
      // stay together when a long name wraps (the dashboard's pencil spot).
      const tools = el('div', 'club-name-tools', row);
      circleButton(
        tools,
        { className: 'club-invite-circle', icon: ADD_PERSON_SVG, label: 'Invite racers' },
        () => this.openInvite(),
      );
      if (c.myRole === 'captain') {
        circleButton(
          tools,
          { className: 'rid-edit club-gear', icon: GEAR_SVG, label: 'Club settings' },
          () => this.openSettings(),
        );
      }
      const captain = o.members.find((m) => m.role === 'captain');
      if (captain) {
        const pill = el('div', 'rid-rank club-captain', copy);
        el('span', 'club-armband', pill).innerHTML = captainBadgeSvg();
        const t = el('span', 'rid-rank-text', pill);
        el('span', 'rid-rank-label', t).textContent = 'CAPTAIN';
        el('span', 'club-captain-name', t).textContent = captain.name.toUpperCase();
      }
      const founded = c.createdAt
        ? new Date(c.createdAt)
            .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
            .toUpperCase()
        : '';
      el('div', 'rid-via club-meta', copy).textContent =
        `${o.members.length}/${c.max} MEMBERS${founded ? ` · FOUNDED ${founded}` : ''}`;
      return;
    }

    const loading = !this.locked && this.crew === null;
    if (loading) {
      el('div', 'rid-welcome club-loading-line', copy).textContent = this.crewFailed
        ? "COULDN'T REACH YOUR CLUB"
        : 'LOADING YOUR CLUB…';
      return;
    }
    // No club yet: the pitch, with a crest that previews the one being made.
    const lockup = el('div', 'club-lockup', copy);
    this.startCrest = el('div', 'club-crest-slot', lockup);
    this.refreshStartCrest();
    const text = el('div', 'club-lockup-text', lockup);
    el('div', 'rid-welcome', text).textContent = 'START YOUR';
    el('div', 'club-name', text).textContent = 'RACER CLUB';
    el('div', 'rid-via club-meta', copy).textContent =
      'RACE YOUR FRIENDS · CLIMB THE CLUB RANKINGS';
  }

  /** The crest a new club will wear (the create panel's pick, else the
   *  soccer-ball default). */
  private startCrestPick(): Crest {
    return this.draftCrest ?? DEFAULT_CREST;
  }

  /** Keep the no-club hero crest in step with the create panel's pick. */
  private refreshStartCrest(): void {
    if (!this.startCrest) return;
    const c = this.startCrestPick();
    this.startCrest.replaceChildren(
      crestEl(c, 'crew-crest club-crest club-crest-start', crestLabel(c)),
    );
  }

  private renderLoading(): void {
    const card = el('div', 'club-panel crew-loading', this.body);
    el('div', 'crew-spinner', card);
    el('div', 'crew-fine', card).textContent = 'LOADING YOUR CLUB…';
  }

  /** Could not reach the server: say so, and offer one clear retry. */
  private renderRetry(parent: HTMLElement): void {
    const card = el('div', 'club-panel crew-loading crew-retry', parent);
    el('div', 'crew-fine', card).textContent = "COULDN'T REACH RACER CLUB. CHECK YOUR CONNECTION.";
    ridButton(
      card,
      { variant: 'gold', label: 'TRY AGAIN', small: true, className: 'crew-retry-btn' },
      () => {
        this.crewFailed = false;
        this.friendsFailed = false;
        this.render();
        this.load();
      },
    );
  }

  // ---- no club: create + join ---------------------------------------------------------------

  private renderNoCrew(): void {
    const grid = el('div', 'club-start-grid', this.body);
    this.renderCreatePanel(grid);
    this.renderJoinPanel(grid);
    const foot = el('div', 'club-foot', this.body);
    footLine(foot, `ONE CLUB PER RACER · UP TO ${CREW_MAX} MEMBERS`);
  }

  private renderCreatePanel(parent: HTMLElement): void {
    const panel = el('div', 'club-panel club-panel-gold club-create', parent);
    panelHead(panel, 'NAME YOUR CLUB');
    const field = el('div', 'crew-name-field', panel);
    const input = sheetInput(field, {
      className: 'crew-input crew-name-input',
      maxLength: CREW_NAME_MAX,
      label: 'Club name',
    });
    const chip = el('span', 'crew-suggest-chip', field);
    chip.textContent = 'SUGGESTED';
    const dice = el('button', 'crew-dice', field);
    dice.type = 'button';
    dice.setAttribute('aria-label', 'Suggest another name');
    dice.innerHTML = DICE_SVG;
    const status = el('div', 'crew-status', panel);
    status.setAttribute('aria-live', 'polite');

    // M142: the CLUB CREST — an icon in two club colours (soccer ball until
    // the creator picks). A pick here is a local draft; CREATE sends it.
    const crestRow = el('div', 'club-crest-pick', panel);
    const crestArt = el('div', 'club-crest-pick-art', crestRow);
    const crestText = el('div', 'club-crest-pick-text', crestRow);
    el('div', 'club-crest-pick-label', crestText).textContent = 'CLUB CREST';
    const crestName = el('div', 'club-crest-pick-name', crestText);
    const crestBtn = ridButton(
      crestRow,
      {
        variant: 'glass',
        label: 'CHOOSE CREST',
        small: true,
        noChevron: true,
        className: 'club-crest-pick-btn',
      },
      () =>
        openCrestPicker({
          current: this.startCrestPick(),
          kicker: 'YOUR NEW CLUB',
          title: 'PICK YOUR CREST',
          saveLabel: 'USE THIS CREST',
          reducedMotion: this.cb.reducedMotion(),
          // A draft only: no RPC (the locked web preview included). CREATE
          // sends the crest with the name.
          onSave: (c) => {
            this.draftCrest = { ...c };
            this.paintCreateCrest?.();
            this.refreshStartCrest();
            return Promise.resolve(null);
          },
        }),
    );
    const paintCrest = (): void => {
      const c = this.startCrestPick();
      crestArt.replaceChildren(crestEl(c, 'crew-crest crew-crest-sm', crestLabel(c)));
      crestName.textContent = crestLabel(c);
      crestBtn.setLabel(this.draftCrest ? 'CHANGE' : 'CHOOSE CREST');
    };
    this.paintCreateCrest = paintCrest;
    paintCrest();

    const create = ridButton(
      panel,
      { variant: 'gold', label: 'CREATE CLUB', icon: SHIELD_SVG, className: 'club-create-btn' },
      () => submit(),
    );
    this.createCtl = { create, crestBtn, status };
    el('div', 'crew-fine', panel).textContent =
      "You'll be the captain. Next: pick your helmet and invite your friends.";

    const showSuggestion = (): void => {
      input.value = this.suggestion;
      field.classList.toggle('suggested', !!this.suggestion);
      if (this.creating) return; // the status line says CREATING… until it lands
      status.className = 'crew-status';
      status.textContent = this.suggestion ? 'TAP THE NAME TO TYPE YOUR OWN' : '';
    };
    const roll = (): void => {
      // Verify a few at once and take the first free one (one round trip).
      const cands = Array.from({ length: 5 }, () => suggestCrewName());
      void this.cb.social.crewNamesAvailable(cands).then((res) => {
        const free = res ? cands.find((c) => res[c] === true) : cands[0];
        this.suggestion = free ?? cands[0];
        if (this.draftName === null) showSuggestion();
      });
    };
    if (this.draftName !== null) {
      input.value = this.draftName;
    } else if (this.suggestion) {
      showSuggestion();
    } else {
      roll();
    }
    if (this.creating) {
      // Rebuilt mid-CREATE (a tab switch, a live reload): stay locked.
      create.el.disabled = true;
      crestBtn.el.disabled = true;
      status.className = 'crew-status';
      status.textContent = 'CREATING…';
    }
    dice.addEventListener('click', () => {
      this.draftName = null;
      roll();
    });
    // Tap into a suggestion = type your own (the M101b username pattern).
    input.addEventListener('focus', () => {
      if (this.draftName === null && field.classList.contains('suggested')) {
        input.value = '';
        field.classList.remove('suggested');
        status.textContent = '3 TO 18 LETTERS, NUMBERS OR SPACES';
      }
    });
    input.addEventListener('blur', () => {
      if (input.value.trim() === '') {
        this.draftName = null;
        showSuggestion();
      }
    });
    let seq = 0;
    input.addEventListener('input', () => {
      const v = tidyCrewName(input.value);
      if (v !== input.value) input.value = v;
      this.draftName = v;
      field.classList.remove('suggested');
      const name = v.trim();
      const mine = ++seq;
      if (!isValidCrewName(name)) {
        status.className = 'crew-status';
        status.textContent = '3 TO 18 LETTERS, NUMBERS OR SPACES';
        return;
      }
      status.className = 'crew-status';
      status.textContent = 'CHECKING…';
      window.setTimeout(() => {
        if (mine !== seq) return;
        void this.cb.social.crewNamesAvailable([name]).then((res) => {
          if (mine !== seq) return;
          const free = res ? res[name] : null;
          status.className = `crew-status ${free === false ? 'bad' : free ? 'ok' : ''}`;
          status.textContent =
            free === false ? '✗ NAME TAKEN OR NOT ALLOWED' : free ? '✓ AVAILABLE' : '';
        });
      }, 450);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });

    const submit = (): void => {
      if (this.creating) return; // CREATE (or Enter) again while it saves
      const name = (this.draftName ?? this.suggestion).trim();
      if (!isValidCrewName(name)) {
        status.className = 'crew-status bad';
        status.textContent = createCrewMessage('invalid');
        return;
      }
      // The crest is locked in with the name: a crest changed while CREATE is
      // in flight would show in the hero and then silently not be the one
      // the club got (review round).
      this.creating = true;
      create.el.disabled = true;
      crestBtn.el.disabled = true;
      status.className = 'crew-status';
      status.textContent = 'CREATING…';
      this.flowBusy = true;
      void this.cb.social.createCrew(name, this.startCrestPick()).then((r) => {
        this.creating = false;
        if (r.status !== 'ok') {
          this.flowBusy = false;
          // The panel on screen now (a re-render may have replaced ours).
          const live = this.createCtl ?? { create, crestBtn, status };
          live.create.el.disabled = false;
          live.crestBtn.el.disabled = false;
          live.status.className = 'crew-status bad';
          live.status.textContent = createCrewMessage(r.status);
          if (r.status === 'taken') this.suggestion = '';
          return;
        }
        sfxCollect();
        hapticImpact();
        this.draftName = null;
        this.draftCrest = null;
        this.suggestion = '';
        this.afterJoin(name, 'created');
      });
    };
  }

  private renderJoinPanel(parent: HTMLElement): void {
    const panel = el('div', 'club-panel crew-joinpanel', parent);
    panelHead(panel, 'JOIN A CLUB');
    const invites = this.locked ? [] : (this.crew?.invites ?? []);
    if (invites.length > 0) {
      const list = el('div', 'crew-invite-list', panel);
      for (const inv of invites) {
        const row = el('div', 'crew-invite', list);
        row.appendChild(crestEl(inv.crest, 'crew-crest crew-crest-sm', crestLabel(inv.crest)));
        const t = el('div', 'crew-invite-text', row);
        el('div', 'crew-invite-name', t).textContent = inv.name.toUpperCase();
        el('div', 'crew-invite-sub', t).textContent =
          `${inv.members}/${CREW_MAX} MEMBERS${inv.inviter ? ` · FROM ${inv.inviter.toUpperCase()}` : ''}`;
        const join = ridButton(
          row,
          {
            variant: 'gold',
            label: 'JOIN',
            small: true,
            noChevron: true,
            className: 'crew-invite-join',
          },
          () => {
            join.el.disabled = true;
            void this.cb.social.respondCrewInvite(inv.crewId, true).then((r) => {
              if (r.status === 'ok') {
                sfxCollect();
                this.afterJoin(inv.name, 'joined');
              } else {
                join.el.disabled = false;
                this.flash(t, joinCrewMessage(r.status));
              }
            });
          },
        );
        const x = el('button', 'crew-x', row);
        x.type = 'button';
        x.setAttribute('aria-label', `Decline the invite from ${inv.name}`);
        x.textContent = '✕';
        x.addEventListener('click', () =>
          this.declineCrewInvite(inv.crewId, inv.name, inv.inviter),
        );
      }
    } else {
      el('div', 'crew-fine crew-join-empty', panel).textContent =
        'No invites yet. Ask a friend for their club link or club code.';
    }
    el('div', 'crew-or', panel).textContent = 'HAVE A CLUB CODE?';
    const row = el('div', 'crew-inline-form', panel);
    const input = sheetInput(row, {
      className: 'crew-input crew-code-input',
      placeholder: 'CLUB CODE',
      maxLength: 120,
      label: 'Club code',
    });
    input.setAttribute('autocapitalize', 'characters');
    const status = el('div', 'crew-status', panel);
    const join = btn('JOIN', 'btn btn-primary crew-inline-btn', row, () => submit());
    const submit = (): void => {
      const code = sanitizeCrewCode(input.value);
      if (!code) {
        input.classList.remove('shake');
        void input.offsetWidth;
        input.classList.add('shake');
        status.className = 'crew-status bad';
        status.textContent = 'A CLUB CODE IS 8 LETTERS AND NUMBERS';
        return;
      }
      join.disabled = true;
      status.className = 'crew-status';
      status.textContent = 'JOINING…';
      void this.cb.social.crewPreview(code).then((p) => {
        if (p.status !== 'ok') {
          join.disabled = false;
          status.className = 'crew-status bad';
          status.textContent = joinCrewMessage(p.status).toUpperCase();
          return;
        }
        void this.cb.social.joinCrewByCode(code).then((r) => {
          join.disabled = false;
          if (r.status === 'ok') {
            sfxCollect();
            input.value = '';
            this.afterJoin(p.name, 'joined');
          } else {
            status.className = 'crew-status bad';
            status.textContent = joinCrewMessage(r.status).toUpperCase();
          }
        });
      });
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });
  }

  /** The step after creating or joining: helmet → (creator: invite) → the
   *  club page, with the iOS push soft-ask at the very end. */
  private afterJoin(crewName: string, how: 'created' | 'joined'): void {
    this.flowBusy = true;
    const seq = ++this.crewSeq;
    void this.cb.social.crewOverview().then((o) => {
      if (seq === this.crewSeq && o) this.crew = o;
      this.paintTabs();
      const me = this.crew?.members.find((m) => m.me);
      const finish = (): void => {
        this.flowBusy = false;
        this.render();
        this.cb.onJoinedCrew?.(crewName);
      };
      openHelmetPicker({
        current: me?.helmet ?? defaultHelmet(this.accountId ?? ''),
        kicker:
          how === 'created' ? 'STEP 2 OF 3 · YOUR HELMET' : `WELCOME TO ${crewName.toUpperCase()}`,
        saveLabel: how === 'created' ? 'SAVE + NEXT' : 'SAVE HELMET',
        skippable: true,
        reducedMotion: this.cb.reducedMotion(),
        onSave: (h) => this.saveHelmet(h),
        onDone: () => {
          if (how !== 'created' || !this.crew?.crew) {
            finish();
            return;
          }
          const c = this.crew.crew;
          openInviteSheet({
            crewName: c.name,
            code: c.code,
            kicker: 'STEP 3 OF 3 · BUILD YOUR CLUB',
            friends: this.friends?.friends ?? [],
            onShareLink: () => this.cb.shareCrewLink(c.name, c.code),
            onCopyCode: () => this.cb.copyText(c.code),
            onInvite: (id) => this.cb.social.inviteToCrew(id),
            onDone: finish,
            reducedMotion: this.cb.reducedMotion(),
          });
        },
      });
      this.render();
    });
  }

  private async saveHelmet(h: Helmet): Promise<string | null> {
    const r = await this.cb.social.setHelmet(h);
    if (r.status !== 'ok') {
      if (r.status === 'not_in_crew') return 'Join a club to wear a club helmet.';
      if (r.status === 'rate') return 'Lots of changes for one hour. Try again a little later.';
      return 'Could not save. Check your connection.';
    }
    // Paint the new helmet at once (the server confirms on the next load).
    if (this.crew) {
      this.crew = {
        ...this.crew,
        members: this.crew.members.map((m) => (m.me ? { ...m, helmet: { ...h } } : m)),
      };
    }
    this.loadCrew();
    return null;
  }

  private declineCrewInvite(crewId: string, crewName: string, inviter: string): void {
    const sheet = openSheet({
      kicker: 'CLUB INVITE',
      title: `DECLINE ${crewName.toUpperCase()}?`,
      reducedMotion: this.cb.reducedMotion(),
    });
    el('div', 'crew-fine', sheet.body).textContent = inviter
      ? `Block ${inviter.toUpperCase()} if you never want their invites or friend requests again.`
      : 'You can still join later with a club link.';
    const actions = el('div', 'crew-sheet-actions crew-sheet-stack', sheet.body);
    ridButton(actions, { variant: 'glass', label: 'DECLINE', noChevron: true }, () => {
      sheet.close();
      void this.cb.social.respondCrewInvite(crewId, false).then(() => this.loadCrew());
    });
    if (inviter) {
      btn(`DECLINE + BLOCK ${inviter.toUpperCase()}`, 'btn btn-danger-ghost', actions, () => {
        sheet.close();
        void this.cb.social.respondCrewInvite(crewId, false, { block: true }).then(() => {
          this.loadCrew();
          this.loadFriends();
        });
      });
    }
    // M156: report the club's name, or the player who sent the invite.
    const links = el('div', 'mod-links', sheet.body);
    moderateLink(
      links,
      () =>
        openPlayerSheet({ deps: this.cb.moderation, name: crewName, kind: 'club', context: 'invite' }),
      'REPORT CLUB NAME',
    );
    if (inviter) {
      moderateLink(
        links,
        () => openPlayerSheet({ deps: this.cb.moderation, name: inviter, context: 'invite' }),
        `REPORT ${inviter.toUpperCase()}`,
      );
    }
  }

  // ---- in a club ------------------------------------------------------------------------------

  private renderCrew(o: CrewOverview): void {
    const c = o.crew;
    if (!c) return;
    const me = o.members.find((m) => m.me);
    const iAmCaptain = c.myRole === 'captain';

    // ---- M142 layout: one grid, built in reading order (race → rankings →
    // stats → helmet, the portrait order). Desktop places the race button at
    // the head of a left column (stats, MY HELMET under it) and the rankings
    // level with it on the right. ----
    const main = el('div', 'club-main', this.body);
    const race = o.race && isCrewRaceFresh(o.race) ? o.race : null;
    const raceBtn = ridButton(
      main,
      race
        ? {
            variant: 'gold',
            icon: FLAG_SVG,
            label: 'JOIN CLUB RACE',
            sub: `${race.starterName.toUpperCase()} · ${race.visibility === 'public' ? 'OPEN' : 'CLUB ONLY'} · ${crewRaceWhat(race)}`,
            className: 'club-race-btn live',
          }
        : {
            variant: 'gold',
            icon: FLAG_SVG,
            label: 'START CLUB RACE',
            sub: 'INVITE THE WHOLE CLUB',
            className: 'club-race-btn',
          },
      () => {
        if (race) {
          this.cb.onJoinRace(race);
          return;
        }
        openClubRaceSheet({
          crewName: c.name,
          reducedMotion: this.cb.reducedMotion(),
          onStart: (choice: CrewRaceChoice) => this.cb.onStartRace(choice),
        });
      },
    );
    if (race) el('span', 'crew-live-dot', raceBtn.el);

    // ---- the CLUB RANKINGS (the dashboard's trophy-cabinet panel) ----
    const rank = el('div', 'club-panel club-rank', main);
    panelHead(rank, 'CLUB RANKINGS');
    const sorts = el('div', 'crew-sorts', rank);
    sorts.setAttribute('role', 'tablist');
    for (const s of CREW_SORTS) {
      const chip = el('button', `crew-sort${s.id === this.sort ? ' on' : ''}`, sorts);
      chip.type = 'button';
      chip.setAttribute('role', 'tab');
      chip.setAttribute('aria-selected', String(s.id === this.sort));
      chip.textContent = s.label;
      chip.addEventListener('click', () => {
        if (this.sort === s.id) return;
        this.sort = s.id;
        this.render();
      });
    }
    const list = el('div', 'crew-rank-list', rank);
    const unit = CREW_SORTS.find((s) => s.id === this.sort)?.unit ?? 'XP';
    rankMembers(o.members, this.sort).forEach((m, i) => {
      const r = el('button', `crew-row${m.me ? ' me' : ''}`, list);
      r.type = 'button';
      r.style.setProperty('--row-i', String(i));
      const pos = el('span', `crew-row-rank${i < 3 ? ` p${i + 1}` : ''}`, r);
      pos.textContent = String(i + 1);
      r.appendChild(helmetEl(m.helmet, 'crew-helmet crew-row-helmet'));
      const rowMain = el('span', 'crew-row-main', r);
      const nameRow = el('span', 'crew-row-namerow', rowMain);
      el('span', 'crew-row-name', nameRow).textContent = m.name.toUpperCase();
      if (m.role === 'captain') {
        const b = el('span', 'crew-armband crew-armband-sm', nameRow);
        b.innerHTML = captainBadgeSvg();
        b.title = 'Captain';
      }
      if (m.me) el('span', 'crew-you', nameRow).textContent = 'YOU';
      const tier = skillTier(m.xp);
      const sub = el('span', 'crew-row-sub', rowMain);
      const tierEl = el('span', 'crew-row-tier', sub);
      tierEl.textContent = tier.name;
      tierEl.style.color = tier.color;
      el('span', '', sub).textContent = ` · ${m.wins} ${m.wins === 1 ? 'WIN' : 'WINS'}`;
      const val = el('span', 'crew-row-val', r);
      el('b', '', val).textContent = fmt(rankValue(m, this.sort));
      el('small', '', val).textContent = unit;
      r.setAttribute(
        'aria-label',
        `${i + 1}. ${m.name}${m.role === 'captain' ? ', captain' : ''}: ${rankValue(m, this.sort)} ${unit}`,
      );
      r.addEventListener('click', () => {
        if (m.me) this.openMyHelmet();
        else this.openMember(m, iAmCaptain);
      });
    });

    // ---- the club stats: four colour-coded modules ----
    const totals = crewTotals(o.members);
    const stats = el('div', 'club-stats', main);
    const stat = (label: string, value: string, unit: string, cls: string): void => {
      const s = el('div', `rid-stat club-stat ${cls}`, stats);
      el('div', 'rid-stat-label', s).textContent = label;
      el('div', 'rid-stat-value', s).textContent = value;
      el('div', 'rid-stat-unit', s).textContent = unit;
    };
    stat('CLUB XP', fmt(totals.xp), 'ALL MEMBERS', 'club-stat-xp');
    stat('WEEKLY XP', fmt(totals.week), 'RESETS MONDAY', 'club-stat-week');
    stat('RACE WINS', fmt(totals.wins), '1ST PLACES', 'club-stat-wins');
    stat('CLUB RACES', fmt(c.races), 'RACED TOGETHER', 'club-stat-races');
    if (me) {
      const hb = el('button', 'club-helmet-card', main);
      hb.type = 'button';
      hb.setAttribute('aria-label', 'Change my helmet');
      hb.appendChild(helmetEl(me.helmet, 'crew-helmet club-helmet-art'));
      const t = el('span', 'club-helmet-text', hb);
      el('span', 'club-helmet-label', t).textContent = 'MY HELMET';
      el('span', 'club-helmet-name', t).textContent = helmetLabel(me.helmet);
      el('span', 'club-helmet-chev', hb).innerHTML = CHEVRON_SVG;
      hb.addEventListener('click', () => {
        sfxUiTick();
        hapticTick();
        this.openMyHelmet();
      });
    }

    // ---- invites from OTHER clubs (rare once you're in one) ----
    if (o.invites.length > 0) {
      const other = el('div', 'club-panel crew-other', this.body);
      panelHead(other, 'INVITES FROM OTHER CLUBS');
      for (const inv of o.invites) {
        const r = el('div', 'crew-invite', other);
        r.appendChild(crestEl(inv.crest, 'crew-crest crew-crest-sm', crestLabel(inv.crest)));
        const t = el('div', 'crew-invite-text', r);
        el('div', 'crew-invite-name', t).textContent = inv.name.toUpperCase();
        el('div', 'crew-invite-sub', t).textContent = `${inv.members}/${CREW_MAX} MEMBERS`;
        ridButton(
          r,
          {
            variant: 'glass',
            label: 'SWITCH',
            small: true,
            noChevron: true,
            className: 'crew-invite-join',
          },
          () =>
            confirmAction({
              title: `SWITCH TO ${inv.name.toUpperCase()}?`,
              body: `You'll leave ${c.name.toUpperCase()}${iAmCaptain ? ' and your captain armband passes to your longest-serving club member' : ''}. You can only be in one club.`,
              confirmLabel: 'SWITCH CLUBS',
              reducedMotion: this.cb.reducedMotion(),
              onConfirm: () => {
                void this.cb.social
                  .respondCrewInvite(inv.crewId, true, { switchCrew: true })
                  .then((r2) => {
                    if (r2.status === 'ok') this.afterJoin(inv.name, 'joined');
                    else this.flash(t, joinCrewMessage(r2.status));
                  });
              },
            }),
        );
        const x = el('button', 'crew-x', r);
        x.type = 'button';
        x.setAttribute('aria-label', `Decline the invite from ${inv.name}`);
        x.textContent = '✕';
        x.addEventListener('click', () =>
          this.declineCrewInvite(inv.crewId, inv.name, inv.inviter),
        );
      }
    }

    // ---- foot: the dashboard's rule line + the quiet LEAVE CLUB ----
    const foot = el('div', 'club-foot', this.body);
    footLine(foot, `ANY MEMBER CAN INVITE · UP TO ${CREW_MAX} MEMBERS`);
    btn('LEAVE CLUB', 'btn btn-danger-ghost club-leave', foot, () => this.confirmLeave(o));
  }

  /** CLUB SETTINGS (captain only) — the hero's gear. */
  private openSettings(): void {
    const c = this.crew?.crew;
    if (!c || c.myRole !== 'captain') return;
    openCrewSettings({
      crewName: c.name,
      crest: c.crest,
      // Opening the picker replaces the settings sheet (one sheet at a time).
      onChangeCrest: () => this.openClubCrest(),
      reducedMotion: this.cb.reducedMotion(),
      onCheckName: async (n) => {
        const res = await this.cb.social.crewNamesAvailable([n]);
        return res ? (res[n] ?? null) : null;
      },
      onRename: async (n) => {
        const r = await this.cb.social.renameCrew(n);
        if (r.status === 'ok') this.loadCrew();
        return r;
      },
      onResetLink: async () => {
        const r = await this.cb.social.resetCrewCode();
        if (r.status === 'ok') this.loadCrew();
        return r;
      },
    });
  }

  /** The captain re-picks the club crest (CLUB SETTINGS → CHANGE CREST). */
  private openClubCrest(): void {
    const c = this.crew?.crew;
    if (!c || c.myRole !== 'captain' || this.locked) return;
    openCrestPicker({
      current: c.crest,
      kicker: 'CAPTAIN ONLY',
      title: 'CLUB CREST',
      saveLabel: 'SAVE CREST',
      reducedMotion: this.cb.reducedMotion(),
      onSave: async (crest) => {
        const r = await this.cb.social.setCrewCrest(crest);
        if (r.status !== 'ok') {
          if (r.status === 'rate') return 'Lots of changes for one hour. Try again a little later.';
          if (r.status === 'not_captain') return 'Only the club captain can change the crest.';
          if (r.status === 'not_in_crew') return "You're not in this club anymore.";
          if (r.status === 'invalid') return 'That crest could not be saved. Pick again.';
          return 'Could not save. Check your connection.';
        }
        // Paint the new crest at once (the server confirms on the next load).
        if (this.crew?.crew) {
          this.crew = { ...this.crew, crew: { ...this.crew.crew, crest: { ...crest } } };
          if (this.isVisible && !this.flowBusy) this.renderHero();
        }
        this.loadCrew();
        return null;
      },
    });
  }

  private openInvite(): void {
    const c = this.crew?.crew;
    if (!c) return;
    const inCrew = new Set(this.crew?.members.map((m) => m.userId));
    const friends = (this.friends?.friends ?? []).filter((f) => !inCrew.has(f.userId));
    openInviteSheet({
      crewName: c.name,
      code: c.code,
      friends,
      reducedMotion: this.cb.reducedMotion(),
      onShareLink: () => this.cb.shareCrewLink(c.name, c.code),
      onCopyCode: () => this.cb.copyText(c.code),
      onInvite: async (id) => {
        const r = await this.cb.social.inviteToCrew(id);
        if (r.status === 'sent') this.loadFriends();
        return r;
      },
    });
  }

  private openMyHelmet(): void {
    const me = this.crew?.members.find((m) => m.me);
    if (!me) return;
    openHelmetPicker({
      current: me.helmet,
      reducedMotion: this.cb.reducedMotion(),
      onSave: (h) => this.saveHelmet(h),
      onDone: (saved) => {
        if (saved) this.render();
      },
    });
  }

  private openMember(m: CrewMember, iAmCaptain: boolean): void {
    openMemberSheet({
      member: m,
      iAmCaptain,
      reducedMotion: this.cb.reducedMotion(),
      onModerate: () =>
        openPlayerSheet({ deps: this.cb.moderation, name: m.name, userId: m.userId, context: 'club' }),
      onAddFriend: async () => {
        const r = await this.cb.social.sendFriendRequest(m.name);
        this.loadFriends();
        return r;
      },
      onMakeCaptain: () =>
        confirmAction({
          title: `MAKE ${m.name.toUpperCase()} CAPTAIN?`,
          body: `${m.name.toUpperCase()} gets the captain's armband and you become a regular member. Only the captain can hand it on.`,
          confirmLabel: 'HAND OVER',
          reducedMotion: this.cb.reducedMotion(),
          onConfirm: () => {
            void this.cb.social.transferCaptain(m.userId).then(() => this.loadCrew());
          },
        }),
      onRemove: () =>
        confirmAction({
          title: `REMOVE ${m.name.toUpperCase()}?`,
          body: `${m.name.toUpperCase()} leaves the club. They can only come back with a new invite or the club link.`,
          confirmLabel: 'REMOVE',
          reducedMotion: this.cb.reducedMotion(),
          onConfirm: () => {
            void this.cb.social.removeMember(m.userId).then(() => this.loadCrew());
          },
        }),
    });
  }

  private confirmLeave(o: CrewOverview): void {
    const c = o.crew;
    if (!c) return;
    const iAmCaptain = c.myRole === 'captain';
    const others = o.members
      .filter((m) => !m.me)
      .sort((a, b) => a.joinedAt - b.joinedAt || (a.userId < b.userId ? -1 : 1));
    const next = others[0];
    const body =
      others.length === 0
        ? `You're the last member, so ${c.name.toUpperCase()} closes for good.`
        : iAmCaptain && next
          ? `Your captain's armband passes to ${next.name.toUpperCase()}, your longest-serving club member.`
          : 'You can come back any time with a club link or an invite.';
    confirmAction({
      title: `LEAVE ${c.name.toUpperCase()}?`,
      body,
      confirmLabel: 'LEAVE CLUB',
      reducedMotion: this.cb.reducedMotion(),
      onConfirm: () => {
        void this.cb.social.leaveCrew().then(() => {
          this.crew = null;
          this.render();
          this.loadCrew();
          this.loadFriends();
        });
      },
    });
  }

  // ---- friends ----------------------------------------------------------------------------------

  private renderFriends(): void {
    const wrap = el('div', 'club-friends', this.body);
    const left = el('div', 'fr-col fr-col-add', wrap);
    const right = el('div', 'fr-col fr-col-list', wrap);

    // ADD A FRIEND — by Racer ID, or share your link.
    const add = el('div', 'club-panel club-panel-gold fr-add', left);
    panelHead(add, 'ADD A FRIEND');
    const row = el('div', 'crew-inline-form', add);
    const input = sheetInput(row, {
      className: 'crew-input',
      placeholder: 'RACER ID',
      maxLength: USERNAME_MAX,
      label: 'Racer ID of your friend',
    });
    const status = el('div', 'crew-status', add);
    status.setAttribute('aria-live', 'polite');
    const addBtn = btn('ADD', 'btn btn-primary crew-inline-btn', row, () => submit());
    const submit = (): void => {
      const v = input.value.trim();
      if (!v) {
        input.focus();
        return;
      }
      addBtn.disabled = true;
      status.className = 'crew-status';
      status.textContent = 'SENDING…';
      void this.cb.social.sendFriendRequest(v).then((r) => {
        addBtn.disabled = false;
        const ok = ['sent', 'accepted', 'already', 'pending'].includes(r.status);
        status.className = `crew-status ${ok ? 'ok' : 'bad'}`;
        status.textContent = friendRequestMessage(r.status, r.name ?? v);
        if (ok) {
          input.value = '';
          this.loadFriends();
        }
      });
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });
    el('div', 'crew-or', add).textContent = 'OR';
    ridButton(
      add,
      { variant: 'share', icon: SHARE_SVG, label: 'SHARE FRIEND LINK', className: 'fr-share' },
      () => {
        void this.cb.shareFriendLink().then((how) => {
          if (how === 'copied') shareNote.textContent = 'LINK COPIED. SEND IT ANYWHERE';
          else if (how === 'shared') shareNote.textContent = 'SENT!';
        });
      },
    );
    const shareNote = el('div', 'crew-fine fr-share-note', add);
    shareNote.textContent = 'Anyone who opens it can add you in one tap.';

    const foot = el('div', 'club-foot', this.body);
    footLine(foot, 'FRIENDS CAN INVITE EACH OTHER TO THEIR CLUBS');

    const f = this.locked ? null : this.friends;
    if (!f) {
      const box = el('div', 'club-panel fr-list', right);
      panelHead(box, 'FRIENDS');
      if (this.locked) {
        el('div', 'crew-empty', box).textContent = 'Friends you add show up here.';
      } else if (this.friendsFailed) {
        box.remove();
        this.renderRetry(right);
      } else {
        el('div', 'crew-spinner', box);
      }
      return;
    }

    // REQUESTS (incoming)
    if (f.incoming.length > 0) {
      const sec = el('div', 'club-panel fr-requests', left);
      panelHead(sec, `REQUESTS (${f.incoming.length})`);
      for (const q of f.incoming) {
        const r = el('div', 'fr-row', sec);
        r.appendChild(helmetEl(q.helmet, 'crew-helmet crew-helmet-sm'));
        const t = el('div', 'fr-row-text', r);
        el('div', 'fr-row-name', t).textContent = q.name.toUpperCase();
        const tier = skillTier(q.xp);
        const sub = el('div', 'fr-row-sub', t);
        sub.textContent = `${tier.name} · ${fmt(q.xp)} XP`;
        const accept = ridButton(
          r,
          {
            variant: 'gold',
            label: 'ACCEPT',
            small: true,
            noChevron: true,
            className: 'fr-row-btn',
          },
          () => {
            accept.el.disabled = true;
            void this.cb.social.respondFriendRequest(q.userId, true).then(() => this.loadFriends());
          },
        );
        const x = el('button', 'crew-x', r);
        x.type = 'button';
        x.setAttribute('aria-label', `Decline ${q.name}`);
        x.textContent = '✕';
        x.addEventListener('click', () => this.declineFriend(q.userId, q.name));
      }
    }

    // FRIENDS
    const sec = el('div', 'club-panel fr-list', right);
    panelHead(sec, `FRIENDS (${f.friends.length})`);
    if (f.friends.length === 0) {
      el('div', 'crew-empty', sec).textContent =
        'No friends yet. Add one by their Racer ID, or share your friend link.';
    }
    const myCrew = this.crew?.crew ?? null;
    for (const fr of f.friends) this.friendRow(sec, fr, myCrew !== null);

    // SENT requests
    if (f.outgoing.length > 0) {
      const sent = el('div', 'club-panel fr-sent', right);
      panelHead(sent, 'WAITING FOR AN ANSWER');
      for (const o of f.outgoing) {
        const r = el('div', 'fr-row fr-row-sent', sent);
        el('div', 'fr-row-name', r).textContent = o.name.toUpperCase();
        ridButton(
          r,
          {
            variant: 'glass',
            label: 'CANCEL',
            small: true,
            noChevron: true,
            className: 'fr-row-btn',
          },
          () => {
            void this.cb.social.removeFriend(o.userId).then(() => this.loadFriends());
          },
        );
      }
    }
  }

  private friendRow(parent: HTMLElement, fr: FriendRow, iHaveCrew: boolean): void {
    const r = el('div', 'fr-row', parent);
    r.appendChild(helmetEl(fr.helmet, 'crew-helmet crew-helmet-sm'));
    const t = el('button', 'fr-row-text fr-row-tap', r);
    t.type = 'button';
    el('span', 'fr-row-name', t).textContent = fr.name.toUpperCase();
    const tier = skillTier(fr.xp);
    const sub = el('span', 'fr-row-sub', t);
    const tierEl = el('span', '', sub);
    tierEl.textContent = tier.name;
    tierEl.style.color = tier.color;
    el('span', '', sub).textContent = fr.crewName
      ? ` · ${fr.crewName.toUpperCase()}`
      : ' · NO CLUB';
    t.addEventListener('click', () => this.openFriend(fr, iHaveCrew));
    if (!iHaveCrew) return;
    if (fr.sameCrew) {
      el('span', 'fr-tag', r).textContent = 'IN YOUR CLUB';
    } else if (fr.invited) {
      el('span', 'fr-tag fr-tag-sent', r).textContent = 'INVITED';
    } else {
      const inv = ridButton(
        r,
        { variant: 'gold', label: 'INVITE', small: true, noChevron: true, className: 'fr-row-btn' },
        () => {
          inv.el.disabled = true;
          void this.cb.social.inviteToCrew(fr.name).then((res) => {
            const ok = res.status === 'sent' || res.status === 'pending';
            inv.setLabel(ok ? 'INVITED' : 'RETRY');
            inv.el.disabled = ok;
            if (!ok) this.flash(t, inviteMessage(res.status, fr.name));
          });
        },
      );
    }
  }

  private openFriend(fr: FriendRow, iHaveCrew: boolean): void {
    const sheet = openSheet({
      kicker: 'FRIEND',
      title: fr.name.toUpperCase(),
      className: 'crew-sheet-member',
      reducedMotion: this.cb.reducedMotion(),
    });
    const card = el('div', 'ms-card', sheet.body);
    card.appendChild(helmetEl(fr.helmet, 'crew-helmet ms-helmet'));
    const stats = el('div', 'ms-stats', card);
    const s1 = el('div', 'ms-stat', stats);
    el('div', 'ms-stat-v', s1).textContent = skillTier(fr.xp).name;
    el('div', 'ms-stat-l', s1).textContent = 'RANK';
    const s2 = el('div', 'ms-stat', stats);
    el('div', 'ms-stat-v', s2).textContent = fmt(fr.xp);
    el('div', 'ms-stat-l', s2).textContent = 'XP';
    const s3 = el('div', 'ms-stat ms-stat-wide', stats);
    el('div', 'ms-stat-v', s3).textContent = fr.crewName ? fr.crewName.toUpperCase() : 'NO CLUB';
    el('div', 'ms-stat-l', s3).textContent = 'CLUB';
    const actions = el('div', 'crew-sheet-actions crew-sheet-stack', sheet.body);
    const status = el('div', 'crew-status', sheet.body);
    if (iHaveCrew && !fr.sameCrew) {
      const inv = ridButton(
        actions,
        {
          variant: 'gold',
          icon: ADD_PERSON_SVG,
          label: fr.invited ? 'INVITED' : 'INVITE TO MY CLUB',
        },
        () => {
          inv.el.disabled = true;
          void this.cb.social.inviteToCrew(fr.name).then((res) => {
            const ok = res.status === 'sent' || res.status === 'pending';
            status.className = `crew-status ${ok ? 'ok' : 'bad'}`;
            status.textContent = inviteMessage(res.status, fr.name);
            inv.setLabel(ok ? 'INVITED' : 'INVITE TO MY CLUB');
            inv.el.disabled = ok;
            if (ok) this.loadFriends();
          });
        },
      );
      if (fr.invited) inv.el.disabled = true;
    }
    btn('REMOVE FRIEND', 'btn btn-danger-ghost', actions, () => {
      sheet.close();
      confirmAction({
        title: `REMOVE ${fr.name.toUpperCase()}?`,
        body: `${fr.name.toUpperCase()} leaves your friends list. You can add each other again any time.`,
        confirmLabel: 'REMOVE FRIEND',
        reducedMotion: this.cb.reducedMotion(),
        onConfirm: () => {
          void this.cb.social.removeFriend(fr.userId).then(() => this.loadFriends());
        },
      });
    });
    // M156: REPORT / BLOCK (blocking also ends the friendship, server-side).
    moderateLink(sheet.body, () =>
      openPlayerSheet({ deps: this.cb.moderation, name: fr.name, userId: fr.userId, context: 'friends' }),
    );
  }

  private declineFriend(userId: string, name: string): void {
    const sheet = openSheet({
      kicker: 'FRIEND REQUEST',
      title: `DECLINE ${name.toUpperCase()}?`,
      reducedMotion: this.cb.reducedMotion(),
    });
    el('div', 'crew-fine', sheet.body).textContent =
      `Block ${name.toUpperCase()} if you never want their requests or club invites again.`;
    const actions = el('div', 'crew-sheet-actions crew-sheet-stack', sheet.body);
    ridButton(actions, { variant: 'glass', label: 'DECLINE', noChevron: true }, () => {
      sheet.close();
      void this.cb.social.respondFriendRequest(userId, false).then(() => this.loadFriends());
    });
    btn(`DECLINE + BLOCK ${name.toUpperCase()}`, 'btn btn-danger-ghost', actions, () => {
      sheet.close();
      void this.cb.social.respondFriendRequest(userId, false, true).then(() => {
        this.loadFriends();
        this.loadCrew();
      });
    });
    // M156: report the request's sender (an offensive name, harassment).
    moderateLink(
      sheet.body,
      () => openPlayerSheet({ deps: this.cb.moderation, name, userId, context: 'request' }),
      `REPORT ${name.toUpperCase()}`,
    );
  }

  /** A short inline error that fades (row-level feedback). */
  private flash(host: HTMLElement, text: string): void {
    const n = el('div', 'crew-flash', host);
    n.textContent = text;
    window.setTimeout(() => n.remove(), 3200);
  }

  dispose(): void {
    this.root.remove();
  }
}
